import API from '../config/api';
import React, { useEffect, useState } from 'react';

import axios from 'axios';

import { Container, Table, Card, Form, Image } from 'react-bootstrap';

import { Link } from 'react-router-dom';

import { toast } from 'react-toastify';

import { confirmToast } from '../utils/toast';

import CustomNavbar from '../components/CustomNavbar';

/* At or below this many units a product is "running low" rather than stocked —
   drives the amber state on the stock chip and the catalogue summary. */
const LOW_STOCK = 5;

const stockStateOf = (stock) => {
  if (!stock || stock <= 0) return 'out';

  return stock <= LOW_STOCK ? 'low' : 'ok';
};

/* The row's accent colour is derived from what the row *is*, so scanning the
   table tells you the state before you read any text. */
const accentOf = (product) => {
  if (product.heroProduct) return '#7c3aed';
  if (product.topSale) return '#f59e0b';

  const state = stockStateOf(product.stock);

  if (state === 'out') return '#dc2626';
  if (state === 'low') return '#ea580c';

  return '#10b981';
};

function AdminProductsPage() {
  const [products, setProducts] = useState([]);

  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const { data } = await axios.get(`${API}/products`);

      setProducts(data);
    } catch (error) {
      console.log(error);
    }
  };

  const handleTopSale = async (id) => {
    try {
      await axios.put(`${API}/products/${id}/topsale`);

      fetchProducts();
    } catch (error) {
      console.log(error);

      toast.error('Failed To Update Top Sale');
    }
  };

  const deleteProduct = async (id) => {
    try {
      await axios.delete(`${API}/products/${id}`);

      toast.success('Product Deleted Successfully');

      fetchProducts();
    } catch (error) {
      console.log(error);
    }
  };

  const setHeroProduct = async (id) => {
    try {
      await axios.put(`${API}/products/hero/${id}`);

      fetchProducts();
    } catch (error) {
      console.log(error);
    }
  };

  const filteredProducts = products.filter(
    (product) =>
      product.name?.toLowerCase().includes(search.toLowerCase()) ||
      product.category?.toLowerCase().includes(search.toLowerCase())
  );

  /* Live catalogue summary — each chip carries its own accent */
  const stats = [
    { key: 'total', label: 'Products', value: products.length, tone: 'violet' },
    {
      key: 'stocked',
      label: 'Stocked',
      value: products.filter((p) => stockStateOf(p.stock) === 'ok').length,
      tone: 'green'
    },
    {
      key: 'low',
      label: 'Running low',
      value: products.filter((p) => stockStateOf(p.stock) === 'low').length,
      tone: 'amber'
    },
    {
      key: 'out',
      label: 'Out of stock',
      value: products.filter((p) => stockStateOf(p.stock) === 'out').length,
      tone: 'red'
    },
    {
      key: 'top',
      label: 'Top sale',
      value: products.filter((p) => p.topSale).length,
      tone: 'gold'
    }
  ];

  return (
    <>
      <CustomNavbar />

      <div className="admin-products-wrap">
        <Container>
          {/* Header */}
          <Card className="border-0 shadow-lg mb-4 ap-hero">
            <span className="ap-hero-aurora" aria-hidden="true" />

            {/* Diya motes drifting up through the banner */}
            <span className="ap-motes" aria-hidden="true">
              {Array.from({ length: 8 }).map((_, i) => (
                <span className="ap-mote" key={i} />
              ))}
            </span>

            <Card.Body className="position-relative d-flex flex-wrap justify-content-between align-items-center gap-3 p-4">
              <div>
                <h2 className="fw-bold mb-1 ap-hero-title">
                  <span className="ap-hero-icon">📦</span>
                  <span className="ap-hero-title-text">Product Management</span>
                </h2>

                <p className="mb-0 ap-hero-text">
                  {products.length} products in your catalogue
                </p>

                <div className="ap-stats">
                  {stats.map((stat) => (
                    <div
                      className={`ap-stat is-${stat.tone}`}
                      key={stat.key}
                      title={stat.label}
                    >
                      <span className="ap-stat-value">{stat.value}</span>
                      <span className="ap-stat-label">{stat.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <Link to="/admin/add-product" className="ap-add-btn">
                <span className="ap-add-plus" aria-hidden="true">
                  +
                </span>
                Add Product
              </Link>
            </Card.Body>
          </Card>

          {/* Search */}
          <div className="ap-search-row">
            <div className="ap-search">
              <span className="ap-search-icon" aria-hidden="true">
                🔍
              </span>

              <Form.Control
                className="ap-search-input"
                placeholder="Search products by name or category..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              {search && (
                <button
                  type="button"
                  className="ap-search-clear"
                  aria-label="Clear search"
                  onClick={() => setSearch('')}
                >
                  ×
                </button>
              )}
            </div>

            <span className="ap-result-count">
              <span className="ap-result-num">{filteredProducts.length}</span>
              shown
            </span>
          </div>

          {/* Table */}
          <Card className="border-0 shadow-sm ap-table-card">
            <Card.Body className="p-0">
              <Table hover responsive className="align-middle mb-0 ap-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Price</th>
                    <th>Stock</th>
                    <th className="text-center">Top Sale</th>
                    <th className="text-center">Hero Product</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredProducts.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-5">
                        <div className="ap-empty">
                          <span className="ap-empty-icon">🪔</span>
                          <div className="fw-bold">No products found</div>
                          <small className="text-muted">
                            Try a different search, or add a new product.
                          </small>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredProducts.map((product, index) => {
                      const state = stockStateOf(product.stock);

                      return (
                        <tr
                          key={product._id}
                          className={`ap-row is-${state}`}
                          style={{
                            '--row-accent': accentOf(product),
                            /* capped so row 40 doesn't wait two seconds */
                            '--i': Math.min(index, 14)
                          }}
                        >
                          <td>
                            <div className="d-flex align-items-center gap-3">
                              <span className="ap-thumb-wrap">
                                <Image
                                  src={product.image}
                                  alt={product.name}
                                  className="ap-thumb"
                                />
                              </span>

                              <div className="ap-name-cell">
                                <div className="fw-semibold ap-name">
                                  {product.name}
                                </div>
                                <small className="ap-category">
                                  {product.category}
                                </small>
                              </div>
                            </div>
                          </td>

                          <td>
                            <span className="ap-price">₹{product.price}</span>
                          </td>

                          <td>
                            <span className={`ap-stock is-${state}`}>
                              <span
                                className="ap-stock-dot"
                                aria-hidden="true"
                              />
                              {state === 'out'
                                ? 'Out of stock'
                                : state === 'low'
                                  ? `Only ${product.stock} left`
                                  : `${product.stock} available`}
                            </span>
                          </td>

                          {/* Top Sale */}
                          <td className="text-center">
                            <button
                              type="button"
                              className={`ap-toggle ap-toggle-sale${
                                product.topSale ? ' is-on' : ''
                              }`}
                              aria-pressed={!!product.topSale}
                              onClick={() => handleTopSale(product._id)}
                            >
                              <span className="ap-toggle-ico" aria-hidden="true">
                                {product.topSale ? '🔥' : '＋'}
                              </span>
                              {product.topSale ? 'Top Sale' : 'Add'}
                            </button>
                          </td>

                          {/* Hero Product */}
                          <td className="text-center">
                            <button
                              type="button"
                              className={`ap-toggle ap-toggle-hero${
                                product.heroProduct ? ' is-on' : ''
                              }`}
                              aria-pressed={!!product.heroProduct}
                              onClick={() => setHeroProduct(product._id)}
                            >
                              <span className="ap-toggle-ico" aria-hidden="true">
                                ⭐
                              </span>
                              {product.heroProduct ? 'Active' : 'Set Hero'}
                            </button>
                          </td>

                          {/* Actions */}
                          <td className="text-center">
                            <div className="d-flex gap-2 justify-content-center">
                              <Link
                                to={`/admin/edit-product/${product._id}`}
                                className="ap-act ap-act-edit"
                              >
                                <span
                                  className="ap-act-ico"
                                  aria-hidden="true"
                                >
                                  ✏️
                                </span>
                                Edit
                              </Link>

                              <button
                                type="button"
                                className="ap-act ap-act-del"
                                onClick={async () => {
                                  if (
                                    await confirmToast('Delete this product?')
                                  ) {
                                    deleteProduct(product._id);
                                  }
                                }}
                              >
                                <span
                                  className="ap-act-ico"
                                  aria-hidden="true"
                                >
                                  🗑
                                </span>
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Container>
      </div>

      <style>{adminProductsStyles}</style>
    </>
  );
}

const adminProductsStyles = `
  .admin-products-wrap {
    background: radial-gradient(720px 320px at 8% -5%, rgba(124,58,237,0.12), transparent 70%), radial-gradient(720px 320px at 92% 105%, rgba(234,88,12,0.14), transparent 70%), linear-gradient(180deg, #faf5ff 0%, #fff7ed 55%, #fef3c7 100%);
    min-height: 100vh;
    padding: clamp(24px, 4vw, 50px) 0;
  }

  /* ===== Sanctum banner ===== */
  .ap-hero {
    position: relative;
    overflow: hidden;
    isolation: isolate;
    border-radius: 22px;
    color: #fff7ed;
    background: linear-gradient(120deg, #1a0611, #460d16 30%, #7c2d12 62%, #b45309 85%, #d97706);
    background-size: 220% 220%;
    border: 1px solid rgba(253, 230, 138, 0.28) !important;
    animation: apHeroDrift 18s ease-in-out infinite alternate;
  }
  @keyframes apHeroDrift {
    from { background-position: 0% 50%; }
    to   { background-position: 100% 50%; }
  }
  /* Gold threads along both edges, running opposite ways */
  .ap-hero::before,
  .ap-hero::after {
    content: '';
    position: absolute;
    left: 0; right: 0;
    height: 2px;
    z-index: 3;
    background: linear-gradient(90deg, transparent, #fde68a 20%, #f59e0b 50%, #fde68a 80%, transparent);
    background-size: 220% 100%;
    animation: apThread 3.6s linear infinite;
  }
  .ap-hero::before { top: 0; }
  .ap-hero::after { bottom: 0; animation-direction: reverse; }
  @keyframes apThread {
    to { background-position: 220% 0; }
  }

  .ap-hero-aurora {
    position: absolute;
    inset: -40%;
    z-index: 0;
    pointer-events: none;
    background:
      radial-gradient(36% 44% at 24% 30%, rgba(251,191,36,0.42), transparent 62%),
      radial-gradient(32% 40% at 78% 26%, rgba(168,85,247,0.32), transparent 62%),
      radial-gradient(40% 46% at 58% 84%, rgba(234,88,12,0.40), transparent 62%);
    filter: blur(30px);
    animation: apAurora 22s ease-in-out infinite alternate;
    will-change: transform;
  }
  @keyframes apAurora {
    0%   { transform: translate3d(-4%, -3%, 0) rotate(0deg)  scale(1.05); }
    50%  { transform: translate3d(5%, 4%, 0)   rotate(8deg)  scale(1.18); }
    100% { transform: translate3d(-3%, 5%, 0)  rotate(-6deg) scale(1.1); }
  }

  .ap-motes { position: absolute; inset: 0; z-index: 1; pointer-events: none; overflow: hidden; }
  .ap-mote {
    position: absolute;
    bottom: -12px;
    width: 6px; height: 6px;
    border-radius: 50%;
    background: rgba(253, 230, 138, 0.9);
    box-shadow: 0 0 12px rgba(251, 191, 36, 0.9);
    opacity: 0;
    animation: apMote 12s linear infinite;
  }
  .ap-mote:nth-child(1) { left: 7%;  animation-delay: 0s; }
  .ap-mote:nth-child(2) { left: 20%; animation-delay: 4.2s; width: 4px; height: 4px; }
  .ap-mote:nth-child(3) { left: 33%; animation-delay: 1.7s; }
  .ap-mote:nth-child(4) { left: 46%; animation-delay: 7.1s; width: 5px; height: 5px; }
  .ap-mote:nth-child(5) { left: 59%; animation-delay: 2.8s; }
  .ap-mote:nth-child(6) { left: 72%; animation-delay: 9.3s; width: 4px; height: 4px; }
  .ap-mote:nth-child(7) { left: 85%; animation-delay: 5.5s; }
  .ap-mote:nth-child(8) { left: 95%; animation-delay: 3.3s; width: 5px; height: 5px; }
  @keyframes apMote {
    0%   { opacity: 0; transform: translateY(0) translateX(0) scale(0.6); }
    12%  { opacity: 1; }
    80%  { opacity: 0.6; }
    100% { opacity: 0; transform: translateY(-220px) translateX(24px) scale(1.1); }
  }

  .ap-hero .card-body { z-index: 2; }
  .ap-hero-title { display: flex; align-items: center; gap: 10px; }
  .ap-hero-icon {
    display: inline-block;
    animation: apIconBob 3.2s ease-in-out infinite;
  }
  @keyframes apIconBob {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    50%      { transform: translateY(-6px) rotate(-7deg); }
  }
  /* Light travelling through the letters */
  .ap-hero-title-text {
    background: linear-gradient(100deg,
      #fff7ed 0%, #fff7ed 32%, #fde68a 46%, #fbbf24 52%, #fde68a 58%, #fff7ed 74%, #fff7ed 100%);
    background-size: 250% 100%;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
    animation: apTitleShine 5.5s ease-in-out infinite;
  }
  @keyframes apTitleShine {
    0%, 100% { background-position: 130% 0; }
    50%      { background-position: -30% 0; }
  }
  .ap-hero-text { opacity: 0.82; }

  /* Live summary chips — each carries its own accent */
  .ap-stats {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 14px;
  }
  .ap-stat {
    display: inline-flex;
    align-items: baseline;
    gap: 7px;
    padding: 6px 13px;
    border-radius: 30px;
    font-size: 0.72rem;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(253, 230, 138, 0.28);
    backdrop-filter: blur(6px);
    transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
      background 0.25s ease, border-color 0.25s ease;
  }
  .ap-stat:hover {
    transform: translateY(-3px);
    background: rgba(255, 255, 255, 0.18);
    border-color: rgba(253, 230, 138, 0.6);
  }
  .ap-stat-value { font-size: 1rem; font-weight: 800; line-height: 1; }
  .ap-stat-label { opacity: 0.75; letter-spacing: 0.4px; }
  .ap-stat.is-violet .ap-stat-value { color: #d8b4fe; }
  .ap-stat.is-green  .ap-stat-value { color: #6ee7b7; }
  .ap-stat.is-amber  .ap-stat-value { color: #fcd34d; }
  .ap-stat.is-red    .ap-stat-value { color: #fca5a5; }
  .ap-stat.is-gold   .ap-stat-value { color: #fde68a; }
  /* Anything actually out of stock earns a heartbeat */
  .ap-stat.is-red { animation: apAlert 2.4s ease-in-out infinite; }
  @keyframes apAlert {
    0%, 100% { box-shadow: 0 0 0 0 rgba(248, 113, 113, 0.45); }
    50%      { box-shadow: 0 0 0 7px rgba(248, 113, 113, 0); }
  }

  /* ===== Add button ===== */
  .ap-add-btn {
    position: relative;
    overflow: hidden;
    isolation: isolate;
    display: inline-flex;
    align-items: center;
    gap: 9px;
    border: 1px solid rgba(253, 230, 138, 0.7);
    border-radius: 50px;
    padding: 11px 24px;
    font-weight: 700;
    text-decoration: none;
    color: #431407;
    background: linear-gradient(135deg, #fde68a, #fbbf24 55%, #f59e0b);
    box-shadow: 0 10px 24px rgba(245, 158, 11, 0.4);
    transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
      box-shadow 0.25s ease;
  }
  .ap-add-btn:hover {
    color: #431407;
    transform: translateY(-3px);
    box-shadow: 0 16px 32px rgba(245, 158, 11, 0.55);
  }
  .ap-add-btn:active { transform: translateY(-1px) scale(0.97); }
  .ap-add-plus {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px; height: 20px;
    border-radius: 50%;
    background: rgba(67, 20, 7, 0.14);
    font-size: 1.05rem;
    line-height: 1;
    transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  .ap-add-btn:hover .ap-add-plus { transform: rotate(180deg) scale(1.15); }

  /* ===== Search ===== */
  .ap-search-row {
    display: flex;
    align-items: center;
    gap: 14px;
    flex-wrap: wrap;
    margin-bottom: 20px;
  }
  .ap-search {
    position: relative;
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1 1 340px;
    max-width: 480px;
    padding: 4px 14px;
    border-radius: 50px;
    background: #fff;
    border: 2px solid rgba(245, 158, 11, 0.26);
    box-shadow: 0 10px 28px rgba(194, 65, 12, 0.1);
    transition: border-color 0.25s ease, box-shadow 0.25s ease;
  }
  .ap-search:focus-within {
    border-color: #f59e0b;
    box-shadow: 0 14px 36px rgba(245, 158, 11, 0.28);
  }
  .ap-search-icon { flex: none; font-size: 1rem; }
  .ap-search-input {
    border: none !important;
    padding: 9px 0;
    box-shadow: none !important;
    background: transparent;
  }
  .ap-search-clear {
    flex: none;
    border: none;
    width: 22px; height: 22px;
    border-radius: 50%;
    line-height: 1;
    font-size: 1.05rem;
    color: #b45309;
    background: #fff7ed;
    cursor: pointer;
    transition: background 0.2s ease, transform 0.2s ease;
  }
  .ap-search-clear:hover { background: #fde68a; transform: rotate(90deg); }
  .ap-result-count {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-size: 0.8rem;
    font-weight: 600;
    color: #9a3412;
  }
  .ap-result-num {
    font-weight: 800;
    color: #fff;
    background: linear-gradient(135deg, #ea580c, #f59e0b);
    border-radius: 30px;
    padding: 2px 11px;
    box-shadow: 0 4px 12px rgba(234, 88, 12, 0.3);
  }

  /* ===== Table ===== */
  .ap-table-card {
    border-radius: 20px;
    overflow: hidden;
    border: 1px solid rgba(245, 158, 11, 0.16) !important;
  }
  .ap-table thead tr {
    background: linear-gradient(120deg, #3b0764, #7c2d12 55%, #b45309);
  }
  .ap-table thead th {
    position: sticky;
    top: 0;
    z-index: 3;
    background: linear-gradient(120deg, #3b0764, #7c2d12 55%, #b45309);
    border-bottom: none;
    color: #fde68a;
    font-size: 0.74rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 1px;
    padding: 15px 16px;
    white-space: nowrap;
  }
  .ap-table tbody td {
    padding: 12px 16px;
    border-color: #f6eee2;
    background: transparent;
  }

  /* Rows deal themselves in, staggered by --i */
  .ap-row {
    position: relative;
    animation: apRowIn 0.45s cubic-bezier(0.22, 1, 0.36, 1) backwards;
    animation-delay: calc(var(--i, 0) * 45ms);
  }
  @keyframes apRowIn {
    from { opacity: 0; transform: translateY(12px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  /* Accent bar, coloured by the row's own state */
  .ap-row td:first-child { position: relative; }
  .ap-row td:first-child::before {
    content: '';
    position: absolute;
    left: 0; top: 6px; bottom: 6px;
    width: 4px;
    border-radius: 0 4px 4px 0;
    background: var(--row-accent, #e5e7eb);
    transform: scaleY(0);
    transform-origin: center;
    transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
  }
  .ap-row:hover td:first-child::before { transform: scaleY(1); }
  /* Bootstrap's .table-hover paints its own hover, so this needs to win */
  .ap-table.table > tbody > tr.ap-row:hover > td {
    background: linear-gradient(90deg,
      color-mix(in srgb, var(--row-accent, #ea580c) 11%, #ffffff),
      #ffffff 60%);
  }
  /* Fallback for browsers without color-mix */
  @supports not (background: color-mix(in srgb, red 10%, blue)) {
    .ap-table.table > tbody > tr.ap-row:hover > td { background: #fff7ed; }
  }

  .ap-thumb-wrap {
    position: relative;
    flex-shrink: 0;
    display: inline-block;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(124, 45, 18, 0.16);
    transition: box-shadow 0.3s ease, transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  .ap-row:hover .ap-thumb-wrap {
    transform: translateY(-2px) scale(1.06);
    box-shadow: 0 10px 22px color-mix(in srgb, var(--row-accent, #ea580c) 40%, transparent);
  }
  .ap-thumb {
    display: block;
    width: 52px;
    height: 52px;
    object-fit: cover;
    transition: transform 0.4s ease;
  }
  .ap-row:hover .ap-thumb { transform: scale(1.12); }

  .ap-name-cell { min-width: 0; }
  .ap-name {
    color: #1f2937;
    transition: color 0.25s ease;
  }
  .ap-row:hover .ap-name { color: var(--row-accent, #ea580c); }
  .ap-category {
    color: #a8a29e;
    font-size: 0.76rem;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }

  .ap-price {
    font-weight: 800;
    font-size: 1.02rem;
    background: linear-gradient(135deg, #047857, #10b981);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
  }

  /* ===== Stock chip — colour follows the actual quantity ===== */
  .ap-stock {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 5px 13px;
    border-radius: 30px;
    font-size: 0.78rem;
    font-weight: 700;
    white-space: nowrap;
    border: 1px solid transparent;
  }
  .ap-stock-dot {
    width: 7px; height: 7px;
    border-radius: 50%;
    background: currentColor;
  }
  .ap-stock.is-ok {
    color: #047857;
    background: #ecfdf5;
    border-color: #a7f3d0;
  }
  .ap-stock.is-low {
    color: #b45309;
    background: #fffbeb;
    border-color: #fcd34d;
  }
  .ap-stock.is-out {
    color: #b91c1c;
    background: #fef2f2;
    border-color: #fecaca;
  }
  /* Only the states that need attention move */
  .ap-stock.is-low .ap-stock-dot,
  .ap-stock.is-out .ap-stock-dot {
    animation: apDot 1.5s ease-in-out infinite;
  }
  @keyframes apDot {
    0%, 100% { opacity: 0.4; transform: scale(0.8); }
    50%      { opacity: 1;   transform: scale(1.3); }
  }

  /* ===== Toggles ===== */
  .ap-toggle {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 6px 15px;
    border-radius: 30px;
    font-size: 0.79rem;
    font-weight: 700;
    cursor: pointer;
    white-space: nowrap;
    background: #fff;
    transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
      box-shadow 0.25s ease, background 0.25s ease, color 0.25s ease,
      border-color 0.25s ease;
  }
  .ap-toggle:hover { transform: translateY(-2px); }
  .ap-toggle:active { transform: translateY(0) scale(0.95); }
  .ap-toggle-ico { display: inline-block; line-height: 1; }

  .ap-toggle-sale {
    border: 1px solid #fcd34d;
    color: #b45309;
  }
  .ap-toggle-sale:hover { background: #fffbeb; }
  .ap-toggle-sale.is-on {
    border-color: transparent;
    color: #fff;
    background: linear-gradient(135deg, #dc2626, #ea580c 55%, #f59e0b);
    box-shadow: 0 6px 16px rgba(234, 88, 12, 0.4);
  }
  /* The flame flickers only while it is actually a top sale */
  .ap-toggle-sale.is-on .ap-toggle-ico {
    animation: apFlicker 1.1s ease-in-out infinite;
  }
  @keyframes apFlicker {
    0%, 100% { transform: scale(1) rotate(-3deg);    opacity: 0.92; }
    30%      { transform: scale(1.18) rotate(5deg);  opacity: 1; }
    62%      { transform: scale(0.94) rotate(-6deg); opacity: 0.85; }
  }

  .ap-toggle-hero {
    border: 1px solid #c4b5fd;
    color: #6d28d9;
  }
  .ap-toggle-hero:hover { background: #f5f3ff; }
  .ap-toggle-hero.is-on {
    border-color: transparent;
    color: #fff;
    background: linear-gradient(135deg, #4c1d95, #7c3aed 55%, #a855f7);
    box-shadow: 0 6px 16px rgba(124, 58, 237, 0.42);
  }
  .ap-toggle-hero.is-on .ap-toggle-ico {
    animation: apStarSpin 4.5s ease-in-out infinite;
  }
  @keyframes apStarSpin {
    0%, 70%, 100% { transform: rotate(0deg) scale(1); }
    80%           { transform: rotate(180deg) scale(1.25); }
    90%           { transform: rotate(360deg) scale(1); }
  }

  /* ===== Row actions ===== */
  .ap-act {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: none;
    border-radius: 10px;
    padding: 6px 14px;
    font-size: 0.79rem;
    font-weight: 700;
    text-decoration: none;
    color: #fff;
    cursor: pointer;
    white-space: nowrap;
    transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
      box-shadow 0.25s ease, filter 0.25s ease;
  }
  .ap-act:hover { transform: translateY(-2px); color: #fff; filter: brightness(1.06); }
  .ap-act:active { transform: translateY(0) scale(0.95); }
  .ap-act-ico { display: inline-block; }

  .ap-act-edit {
    color: #431407;
    background: linear-gradient(135deg, #fde68a, #fbbf24 55%, #f59e0b);
    box-shadow: 0 5px 14px rgba(245, 158, 11, 0.35);
  }
  .ap-act-edit:hover { color: #431407; box-shadow: 0 10px 22px rgba(245, 158, 11, 0.5); }
  .ap-act-edit:hover .ap-act-ico { animation: apWiggle 0.6s ease-in-out infinite; }
  @keyframes apWiggle {
    0%, 100% { transform: rotate(0deg); }
    32%      { transform: rotate(-17deg); }
    66%      { transform: rotate(13deg); }
  }

  .ap-act-del {
    background: linear-gradient(135deg, #991b1b, #dc2626 55%, #ef4444);
    box-shadow: 0 5px 14px rgba(220, 38, 38, 0.32);
  }
  .ap-act-del:hover { box-shadow: 0 10px 22px rgba(220, 38, 38, 0.48); }
  .ap-act-del:hover .ap-act-ico { animation: apShake 0.55s ease-in-out infinite; }
  @keyframes apShake {
    0%, 100% { transform: translateX(0) rotate(0deg); }
    30%      { transform: translateX(-2px) rotate(-14deg); }
    65%      { transform: translateX(2px) rotate(11deg); }
  }

  /* ===== Empty state ===== */
  .ap-empty { display: grid; gap: 4px; justify-items: center; }
  .ap-empty-icon {
    font-size: 2.4rem;
    animation: apIconBob 3s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    .ap-hero,
    .ap-hero::before,
    .ap-hero::after,
    .ap-hero-aurora,
    .ap-mote,
    .ap-hero-icon,
    .ap-hero-title-text,
    .ap-stat.is-red,
    .ap-row,
    .ap-stock-dot,
    .ap-toggle-sale.is-on .ap-toggle-ico,
    .ap-toggle-hero.is-on .ap-toggle-ico,
    .ap-act-edit:hover .ap-act-ico,
    .ap-act-del:hover .ap-act-ico,
    .ap-empty-icon { animation: none !important; }
    .ap-motes { display: none; }
    .ap-hero-title-text {
      -webkit-text-fill-color: #fff7ed;
      color: #fff7ed;
    }
  }
`;

export default AdminProductsPage;
